import { X } from "lucide-react";

const PASTE_SPLIT_RE = /[,\n\r]+/;

import {
  Fragment,
  type InputHTMLAttributes,
  type KeyboardEvent,
  useId,
  useRef,
  useState,
} from "react";
import { Badge } from "@/components/ui/atoms/badge";
import { cn } from "@/lib/utils";

export type TagsInputValidationResult = {
  /** Normalized value to store. Falls back to the trimmed input if omitted. */
  normalizedValue?: string;
  /** Human-readable error message. Undefined means valid. */
  error?: string;
};

type ForwardedInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  | "className"
  | "defaultValue"
  | "disabled"
  | "onBlur"
  | "onChange"
  | "onKeyDown"
  | "onPaste"
  | "placeholder"
  | "type"
  | "value"
>;

export type TagsInputProps = {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  /** Commit the pending input value when the field loses focus. Default: true. */
  addOnBlur?: boolean;
  /**
   * Validate (and optionally normalize) a trimmed input string before it is added.
   * Return `{ error }` to reject with a message, `{ normalizedValue }` to accept
   * with a transformed value, or `{}` to accept as-is.
   */
  validateValue?: (
    trimmed: string,
    currentValues: string[]
  ) => TagsInputValidationResult;
  /**
   * Determine whether `incoming` is a duplicate of an existing `stored` value.
   * Return `true` to reject with the default "Already added" message, or a
   * non-empty string to reject with that string as the error. Defaults to
   * exact string equality with "Already added".
   */
  isDuplicate?: (incoming: string, stored: string) => boolean | string;
  /**
   * Render a custom chip for each committed value. Receives the value and a
   * `remove` callback. When omitted a plain Badge with an X button is rendered.
   * Keys are managed by TagsInput — do not add a `key` prop to the returned element.
   */
  renderValue?: (value: string, remove: () => void) => React.ReactNode;
  className?: string;
  disabled?: boolean;
} & ForwardedInputProps;

export type TagsInputValueProps = {
  value: string;
  onRemove: () => void;
};

export function TagsInputValue({ value, onRemove }: TagsInputValueProps) {
  return (
    <Badge className="gap-1 pr-1" variant="secondary">
      <span className="max-w-[220px] truncate">{value}</span>
      <button
        aria-label={`Remove ${value}`}
        className="ml-0.5 rounded text-muted-foreground hover:text-foreground"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        type="button"
      >
        <X className="size-3" />
      </button>
    </Badge>
  );
}

function defaultRenderValue(value: string, remove: () => void) {
  return <TagsInputValue onRemove={remove} value={value} />;
}

export function TagsInput({
  values,
  onChange,
  placeholder,
  addOnBlur = true,
  validateValue,
  isDuplicate,
  renderValue,
  className,
  disabled = false,
  id,
  ...inputProps
}: TagsInputProps) {
  const generatedInputId = useId();
  const inputId = id ?? generatedInputId;
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);
  const labelRef = useRef<HTMLLabelElement>(null);

  // Always read the latest values to avoid stale closure when onChange is
  // called multiple times in the same render cycle (e.g. blur then chip remove).
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const renderer = renderValue ?? defaultRenderValue;

  const checkDuplicate = (
    normalized: string,
    list: string[]
  ): string | null => {
    for (const v of list) {
      const result = isDuplicate
        ? isDuplicate(normalized, v)
        : normalized === v;
      if (result === true) {
        return "Already added";
      }
      if (typeof result === "string" && result) {
        return result;
      }
    }
    return null;
  };

  const tryAdd = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      return;
    }

    const current = valuesRef.current;
    const result = validateValue ? validateValue(trimmed, current) : {};

    if (result.error) {
      setInputError(result.error);
      return;
    }

    const normalized = result.normalizedValue ?? trimmed;
    const dupMessage = checkDuplicate(normalized, current);

    if (dupMessage) {
      setInputError(dupMessage);
      return;
    }

    onChange([...current, normalized]);
    setInputValue("");
    setInputError(null);
  };

  const removeValue = (target: string) => {
    onChange(valuesRef.current.filter((v) => v !== target));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === ",") && !e.nativeEvent.isComposing) {
      e.preventDefault();
      tryAdd(inputValue);
    } else if (e.key === "Backspace" && inputValue === "") {
      const last = valuesRef.current.at(-1);
      if (last !== undefined) {
        removeValue(last);
      }
    } else {
      setInputError(null);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (!PASTE_SPLIT_RE.test(text)) {
      return;
    }
    e.preventDefault();
    const tokens = text
      .split(PASTE_SPLIT_RE)
      .map((t) => t.trim())
      .filter(Boolean);

    // Accumulate incrementally so dedup works across tokens in the same paste.
    let accumulated = [...valuesRef.current];
    let firstError: string | null = null;

    for (const token of tokens) {
      const result = validateValue ? validateValue(token, accumulated) : {};
      if (result.error) {
        firstError = result.error;
        break;
      }
      const normalized = result.normalizedValue ?? token;
      const dupMessage = checkDuplicate(normalized, accumulated);
      if (dupMessage) {
        firstError = dupMessage;
        break;
      }
      accumulated = [...accumulated, normalized];
    }

    if (accumulated.length > valuesRef.current.length) {
      onChange(accumulated);
    }
    setInputValue("");
    setInputError(firstError);
  };

  return (
    <div className="space-y-1">
      {/*
       * Using <label> as the container gives us native click-to-focus for free:
       * any click in the padding/gap area will focus the associated <input>
       * without needing an onClick handler or any lint suppression.
       */}
      <label
        className={cn(
          "flex min-h-10 w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-3 py-2 text-sm transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
        htmlFor={inputId}
        ref={labelRef}
      >
        {values.map((v) => (
          <Fragment key={v}>{renderer(v, () => removeValue(v))}</Fragment>
        ))}
        <input
          {...inputProps}
          className="min-w-32 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          disabled={disabled}
          id={inputId}
          onBlur={(e) => {
            // Only commit when focus leaves the component entirely. If focus
            // moves to a chip's remove button (or any other element inside the
            // label), we skip the blur-add so the pending click can fire first
            // without racing against the onChange call from tryAdd.
            const isInternalFocus = labelRef.current?.contains(e.relatedTarget);
            if (addOnBlur && inputValue.trim() && !isInternalFocus) {
              tryAdd(inputValue);
            }
          }}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={values.length === 0 ? placeholder : ""}
          type="text"
          value={inputValue}
        />
      </label>
      {inputError && <p className="text-destructive text-sm">{inputError}</p>}
    </div>
  );
}
